import { useEffect } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Momentum smooth scrolling (Lenis) synced with GSAP ScrollTrigger.
 * The eased scroll glides between the 100vh sections instead of hard-snapping.
 * Skipped entirely when the user prefers reduced motion, and left to native
 * scrolling on touch devices (syncTouch off) so phones stay responsive.
 */
export default function useSmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const lenis = new Lenis({
      duration: 1.4,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out — weighty glide
      smoothWheel: true,
      wheelMultiplier: 0.95,
      syncTouch: false, // native scrolling on touch
    });

    window.__lenis = lenis;

    // Keep ScrollTrigger's reveal animations in step with Lenis' virtual scroll
    lenis.on('scroll', ScrollTrigger.update);
    const raf = time => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);
}

/** Smoothly scroll to a section, using Lenis when active. */
export function scrollToSection(target, offset = 0) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset, duration: 1.2 });
  else el.scrollIntoView({ behavior: 'smooth' });
}
